import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'

import { useMutation } from '@apollo/client/react'

import {
  Button,
  Flex,
  IconButton,
  Tag as TagComponent,
  TagPlusIcon,
  toast,
} from '@buffer-mono/popcorn'

import { usePostData } from '~publish/components/PostCard/PostCardContext'
import { TagsCombobox } from '~publish/components/TagsCombobox'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { removePostFromMismatchedTagLists } from '~publish/helpers/posts/removePostFromMismatchedTagLists'
import {
  holdPostVisible,
  releasePostVisible,
} from '~publish/helpers/posts/tagEditVisibility'
import { client } from '~publish/legacy/apollo-client'

import { usePostDetailsDialog } from '../PostDialogs/helpers'

import styles from './PostCardTags.module.css'

const MAX_TAGS_PER_POST = 10

export const UPDATE_POST_TAGS = graphql(/* GraphQL */ `
  mutation UpdatePostTags($input: UpdatePostTagsInput!) {
    updatePostTags(input: $input) {
      __typename
      ... on PostActionSuccess {
        post {
          __typename
          id
          tags {
            id
            name
            color
          }
        }
      }
      ... on MutationError {
        message
      }
    }
  }
`)

const TagFields = graphql(/* GraphQL */ `
  fragment TagFields on Tag {
    id
    name
    color
  }
`)

type TagFieldsResult = ResultOf<typeof TagFields>

type TagId = TagFieldsResult['id']

type UpdatePostTagsResult = {
  success: boolean
  error: { message: string } | null
}

function useUpdatePostTags(
  postId: string,
): (tagIds: TagId[]) => Promise<UpdatePostTagsResult> {
  const [updatePostTags] = useMutation(UPDATE_POST_TAGS)

  return async (updatedTagIds: TagId[]) => {
    const existingTags = updatedTagIds.map((id) => {
      const tag = client.cache.readFragment({
        id: client.cache.identify({ __typename: 'Tag', id }),
        fragment: TagFields,
      })
      return tag
    })

    const optimisticTags = existingTags.filter(
      (tag): tag is TagFieldsResult => tag !== null,
    )

    try {
      const { data: responseData, error } = await updatePostTags({
        variables: {
          input: {
            id: postId,
            tags: updatedTagIds,
          },
        },
        optimisticResponse: {
          updatePostTags: {
            __typename: 'PostActionSuccess',
            post: {
              __typename: 'Post',
              id: postId,
              tags: optimisticTags,
            },
          },
        },
        update(cache, { data }) {
          if (data?.updatePostTags.__typename === 'PostActionSuccess') {
            const updatedPost = data.updatePostTags.post

            // Tag-filtered lists keep the post until the menu closes so users can
            // make several edits without losing the row.
            cache.modify({
              id: cache.identify({ __typename: 'Post', id: postId }),
              fields: {
                tags() {
                  return updatedPost.tags
                },
              },
            })
          }
        },
      })

      if (error || !responseData) {
        return {
          success: false,
          error: {
            message: error?.message ?? 'Failed to update post tags',
          },
        }
      }

      const result = responseData.updatePostTags
      if (result.__typename !== 'PostActionSuccess') {
        return {
          success: false,
          error: { message: result.message ?? 'Failed to update post tags' },
        }
      }

      return { success: true, error: null }
    } catch (error) {
      return {
        success: false,
        error: {
          message:
            error instanceof Error
              ? error.message
              : 'Failed to update post tags',
        },
      }
    }
  }
}

/**
 * Read-only stand-in for `PostCardTags` on surfaces too dense for the full
 * editable pills (e.g. Board's cards) — just each tag's color, no name, no
 * add/edit affordance. The name still reaches assistive tech via the list
 * item's own label; the rendered tag itself is hidden from it to avoid
 * announcing the name twice.
 */
export function PostCardTagsCompact(): JSX.Element | null {
  const { tags: postTags } = usePostData()
  if (postTags.length === 0) return null

  return (
    <Flex as="ul" gap="3xs" wrap="wrap" className={styles.compactTags}>
      {postTags.map((tag) => (
        <li key={tag.id} aria-label={tag.name} className={styles.tagItem}>
          <span aria-hidden="true">
            <TagComponent color={tag.color} className={styles.compactTagBar}>
              {tag.name}
            </TagComponent>
          </span>
        </li>
      ))}
    </Flex>
  )
}

export function PostCardTags({
  maxVisibleTags = Infinity,
}: {
  maxVisibleTags?: number
}): JSX.Element {
  const { tags: postTags, id: postId, allowedActions } = usePostData()
  const [selectedTags, setSelectedTags] = useState<TagId[]>([])
  const updateTags = useUpdatePostTags(postId)
  const [open, setOpen] = useState(false)
  const pendingTagUpdates = useRef(new Set<Promise<UpdatePostTagsResult>>())
  const tagEditSession = useRef(0)
  const isTagMenuOpen = useRef(false)
  const listRef = useRef(null)
  const { openDetailsDialog } = usePostDetailsDialog({ postId })
  const expandPostCard = (): void => {
    openDetailsDialog({ cta: 'publish-postCard-tags-seeMore-1' })
  }

  useEffect(() => {
    setSelectedTags(postTags?.map((tag) => tag.id) || [])
  }, [postTags])

  useEffect(() => {
    return (): void => {
      tagEditSession.current += 1
      releasePostVisible(postId)
    }
  }, [postId])

  const finalizeTagEdit = useCallback(
    async (
      session: number,
      updates: Promise<UpdatePostTagsResult>[],
    ): Promise<void> => {
      try {
        await Promise.allSettled(updates)

        if (tagEditSession.current !== session || isTagMenuOpen.current) {
          return
        }

        // Every mutation and optimistic rollback from this edit session has
        // settled, so the cache contains the tags that determine membership.
        removePostFromMismatchedTagLists(client.cache, postId)
      } finally {
        if (tagEditSession.current === session && !isTagMenuOpen.current) {
          releasePostVisible(postId)
        }
      }
    },
    [postId],
  )

  // Keep this post visible while its tag menu is open so several tags can be
  // added without it vanishing from a filtered list.
  const handleOpenChange = (nextOpen: boolean): void => {
    isTagMenuOpen.current = nextOpen
    setOpen(nextOpen)
    if (nextOpen) {
      tagEditSession.current += 1
      holdPostVisible(postId)
      return
    }
    void finalizeTagEdit(
      tagEditSession.current,
      Array.from(pendingTagUpdates.current),
    )
  }

  const selectTags = useCallback(
    async (tags: TagId[]): Promise<void> => {
      setSelectedTags(tags)
      const update = updateTags(tags)
      pendingTagUpdates.current.add(update)
      try {
        const { success, error } = await update

        if (!success && error) {
          toast.error(`Failed to edit tags on post`)
        }
      } finally {
        pendingTagUpdates.current.delete(update)
      }
    },
    [updateTags],
  )

  // We have optimistic updates so we have to keep sorting the tags because the
  // last added item would be in the last position.
  const sortedTags = useMemo(
    () => postTags.toSorted((a, b) => a.name.localeCompare(b.name, 'en')),
    [postTags],
  )
  const visibleTags =
    maxVisibleTags < sortedTags.length
      ? sortedTags.slice(0, maxVisibleTags)
      : sortedTags
  const remainingTagsCount = sortedTags.length - visibleTags.length

  const canEditTags = allowedActions.includes('updatePostTags')
  const tooltipMessage =
    postTags.length === 0 ? 'Add tags' : 'Add and edit tags'

  const label = postTags.length === 0 ? 'Add tags' : 'Add and edit tags'
  return (
    <Flex
      as="ul"
      gap="xs"
      wrap="wrap"
      align="center"
      className={styles.tags}
      ref={listRef}
      aria-label="Tags"
    >
      {visibleTags?.map((tag) => {
        if (!tag) return null
        return (
          <li key={tag.id} className={styles.tagItem} translate="no">
            <TagComponent color={tag.color}>{tag.name}</TagComponent>
          </li>
        )
      })}
      {maxVisibleTags && remainingTagsCount > 0 && (
        <li className={styles.tagItem}>
          <Button onClick={expandPostCard} variant="secondary" size="small">
            +{remainingTagsCount} More...
          </Button>
        </li>
      )}
      {canEditTags && (
        <TagsCombobox
          open={open}
          selectedTags={selectedTags}
          onChange={selectTags}
          onOpenChange={handleOpenChange}
          limit={MAX_TAGS_PER_POST}
          align="start"
          anchor={listRef}
        >
          <IconButton
            label={label}
            tooltip={tooltipMessage}
            size="small"
            className={styles.addTagButton}
          >
            <TagPlusIcon />
          </IconButton>
        </TagsCombobox>
      )}
    </Flex>
  )
}
